/**
 * Product Guarantees — seamless trust-badge marquee (Savor-native).
 * The motion is a CSS keyframe (translateX -50%) in the section stylesheet, so it runs in every
 * browser. This component (a) duplicates the badge set for a seamless loop and (b) reveals the bar.
 * Direction, pause-on-hover and reduced-motion are handled in CSS via data attributes.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class ProductGuaranteesComponent extends Component {
  requiredRefs = ['track', 'set'];
  #io = null;

  connectedCallback() {
    super.connectedCallback();

    const reveals = this.querySelectorAll('.reveal');
    if (prefersReducedMotion()) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      this.#io = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-visible'); this.#io?.unobserve(e.target); }
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => this.#io.observe(el));
      this.#ensureClone();
    }
  }

  updatedCallback() {
    super.updatedCallback?.();
    if (!prefersReducedMotion()) this.#ensureClone();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
  }

  /** Exactly one aria-hidden clone of the set => translateX(-50%) loops seamlessly. */
  #ensureClone() {
    const { track, set } = this.refs;
    if (!track || !set) return;
    track.querySelectorAll('[data-pg-clone]').forEach((n) => n.remove());
    const clone = set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-pg-clone', '');
    clone.removeAttribute('ref');
    track.appendChild(clone);
  }
}

if (!customElements.get('product-guarantees-component')) {
  customElements.define('product-guarantees-component', ProductGuaranteesComponent);
}
